import { useEffect, useState } from "react";
import { init, miniApp, swipeBehavior, viewport } from "@telegram-apps/sdk";
import { getResolvedTheme, onThemeChange, type ResolvedTheme } from "../settings/prefs";
import { ensureSdkPolyfills } from "./launch";
import { isBoardHeightPinned, isTextEntry } from "./board-height";

/**
 * Boot the Mini App in TRUE FULLSCREEN (Bot API 8.0 `requestFullscreen()`) on
 * MOBILE (ios/android) at every entry point — bot DM play button, in-chat opens,
 * the profile "Open" button, direct links, the attach menu — WITH the safe-area
 * discipline that was missing the first time (the app's own header (profile +
 * hamburger) and the bottom nav must sit fully inside the visible area, never under
 * the status bar / Dynamic Island / home indicator). DESKTOP (tdesktop/macos/web)
 * is DELIBERATELY NOT forced fullscreen: there `requestFullscreen()` "covers the
 * entire device screen", stretching the mobile-first layout across the whole monitor
 * — the "not responsive on desktop" bug, which engaged inconsistently between the
 * menu-button "Play" and the main-Mini-App "Open" launch paths. Desktop already
 * opens in a medium maximized window, which IS the responsive look, so we leave it
 * (only `expand()` runs there — a documented no-op on desktop). See `allowFullscreen`.
 * Uses the @telegram-apps/sdk v3 `viewport`:
 *  - `init()` FIRST — mandatory; without it every component call throws (see below).
 *  - `mount()` — request viewport data (async; required before the rest).
 *  - `bindCssVars()` — expose `--tg-viewport-height` / `-stable-height` / `-width`
 *    on <html>, so layout height (`--app-h`) binds to Telegram's real viewport
 *    instead of `100vh` (wrong in the Telegram WebView).
 *  - `requestFullscreen()` — non-blocking, result-handled, one retry on a
 *    transient failure (iOS can drop a request that lands while the client is
 *    still presenting the WebView); UNSUPPORTED/ALREADY_FULLSCREEN are terminal.
 *  - `expand()` — ALWAYS, as the fallback for clients < 8.0 (full height under
 *    Telegram's header where fullscreen is unavailable).
 *  - `swipeBehavior.disableVertical()` — stop the swipe-down that collapses/closes
 *    it (the MinimizeButton chip is the deliberate way OUT of fullscreen).
 *
 * SAFE AREAS — THE PART THAT BROKE LAST TIME: in fullscreen, iOS zeroes the CSS
 * `env(safe-area-inset-*)` values; Telegram instead reports insets over the JS
 * bridge as `safeAreaInsets` (device notch/status bar/home indicator) +
 * `contentSafeAreaInsets` (Telegram's own floating chrome). We publish their
 * per-side SUM as `--tg-safe-{top,right,bottom,left}` on <html>, the App shell
 * pads its top with `max(env(safe-area-inset-top), var(--tg-safe-top))`, and
 * every screen/overlay already consumes the same max() pattern — so the header
 * and nav stay visible and tappable in both fullscreen and windowed states.
 *
 * Every call is guarded by `.isAvailable()` (or try/catch), so this no-ops cleanly
 * outside Telegram / on older clients — where CSS falls back to `100dvh` (--app-h)
 * and the native `env()` safe areas.
 */
export async function setupViewport(platform: string | null): Promise<void> {
  // BEFORE `init()`, not after: `init()` parses the launch params itself, so on an Android
  // WebView below Chrome 93 it throws for the same reason the credential path did — and because
  // every call below is inside a try/catch, mount/bindCssVars/requestFullscreen/the swipe guard
  // would all die SILENTLY there, exactly as they did before `init()` was ever called at all.
  // See `ensureSdkPolyfills` for why this is per-entry-point rather than once at module scope.
  ensureSdkPolyfills();
  // Fullscreen is a MOBILE-only affordance (see the file comment): forcing it on
  // desktop covers the whole monitor and stretched the mobile-first layout (the
  // "not responsive via Play" bug). Only ios/android request it; desktop/web keep
  // their default medium maximized window, which is the responsive layout.
  allowFullscreen = fullscreenAllowedFor(platform);
  // The full-height fix's phone clients — Telegram X (`android_x`) among them. See `fullWindowFixFor`.
  fullWindowPhone = fullWindowFixFor(platform);
  // ── ROOT-CAUSE FIX (the historic "app never expands on iOS" bug) ────────────
  // @telegram-apps/sdk v3 REQUIRES init() before ANY component call: without it
  // every viewport.*/swipeBehavior.*/miniApp.* method throws
  //   "… not initialized. Use the SDK init() function"
  // (string verified in the shipped 3.11.8 dist). init() was never called
  // anywhere in this app, and every call below sits in a try/catch — so mount(),
  // bindCssVars(), expand() and the swipe guard were ALL silently dead in
  // production; the app only ever ran on its CSS fallbacks (100dvh + env()).
  // init() wires the JS bridge exactly once — everything else depends on it.
  // Runs UNCONDITIONALLY on EVERY launch path — menu/play button, the profile
  // card's "Open" button, a direct t.me link, a startapp deep link, the attach
  // menu. Entry-point detection must never gate this: an isTMA()-style pre-check
  // that false-negatives on one entry silently skips expand there (the "wrong
  // size when opened outside the DM" bug). If we truly are outside Telegram,
  // init() throws / the availability guards below all no-op — same clean
  // fallback, no gate.
  try {
    init();
  } catch {
    /* outside Telegram — every guarded call below no-ops */
  }
  // Each step is independent + guarded, so one failing never stops the others
  // (a fragile guard bailing before expand()/bindCssVars() was a past regression).
  try {
    if (viewport.mount.isAvailable()) await viewport.mount();
  } catch {
    /* keep going — some clients still let expand()/bindCssVars() work */
  }
  // --tg-viewport-height / -stable-height / -width (+ the SDK's safe-area vars) on
  // <html>. Requires the viewport to be mounted, hence after mount() above.
  try {
    if (viewport.bindCssVars.isAvailable()) viewport.bindCssVars();
  } catch {
    /* ignore */
  }
  // MOBILE: request true fullscreen (the call no-ops on desktop — see allowFullscreen).
  // Non-blocking; transient failures retry once.
  requestFullscreenWithRetry();
  // DESKTOP: do the OPPOSITE — converge EVERY entry point to the clean windowed medium
  // window that the in-DM menu-button launch already uses. External launches (direct
  // link / attach menu / main Mini App) can arrive ALREADY in fullscreen (Telegram sets
  // it at launch via the link's `mode=fullscreen` flag), which stretches the mobile-
  // first layout across the whole monitor — the "external paths don't match Play on
  // desktop" bug. Our own `requestFullscreen` gate can't undo a state Telegram set, so
  // exit it explicitly: now, on a short settle delay, and if it flips on later. This
  // NEVER runs on mobile (allowFullscreen), so mobile stays fullscreen exactly as-is.
  if (!allowFullscreen) {
    keepWindowedOnDesktop();
    setTimeout(keepWindowedOnDesktop, 300);
    try {
      viewport.isFullscreen.sub(keepWindowedOnDesktop);
    } catch {
      /* signal unavailable outside Telegram / on older clients */
    }
  }
  // ALWAYS expand too: the fallback for clients < 8.0 where fullscreen is
  // unavailable (opens at full height with Telegram keeping its own header).
  try {
    if (viewport.expand.isAvailable()) viewport.expand();
  } catch {
    /* ignore */
  }
  try {
    if (swipeBehavior.mount.isAvailable()) swipeBehavior.mount();
    if (swipeBehavior.disableVertical.isAvailable()) swipeBehavior.disableVertical();
  } catch {
    /* ignore */
  }
  // Signal readiness + paint Telegram's own header bar (shown above the expanded
  // app) in the room colour so the chrome blends with the app instead of the
  // default white/blue. Theme-aware: dark felt in dark, the Royal Mint surface
  // in light — and re-applied whenever the «پوسته» toggle/auto scheme flips.
  try {
    if (miniApp.mountSync.isAvailable()) miniApp.mountSync();
    applyHeaderColor(getResolvedTheme());
    onThemeChange(applyHeaderColor);
    if (miniApp.ready.isAvailable()) miniApp.ready();
  } catch {
    /* fine without */
  }

  // SAFE-AREA → CSS (see the file comment). Publish safeAreaInsets +
  // contentSafeAreaInsets (summed per side) now, and again whenever either SDK
  // signal changes (rotation, viewport state changes, …).
  applySafeAreaVars();
  try {
    viewport.safeAreaInsets.sub(() => applySafeAreaVars(true));
    viewport.contentSafeAreaInsets.sub(() => applySafeAreaVars(true));
  } catch {
    /* signals unavailable outside Telegram */
  }
  /*
   * THE SETTLE. A device whose real top inset IS zero never fires the signal above — the value
   * never changes from the SDK's default — so without this the seeded value would stand for the
   * whole session on a phone that has since stopped needing it (a windowed launch, a client
   * without fullscreen). One late, TRUSTED publish converges it; by 1.5s Telegram has answered if
   * it is going to, and if it has, this is a no-op that re-publishes the same number.
   */
  setTimeout(() => applySafeAreaVars(true), 1500);

  // First paint happened with the FALLBACK height; bindCssVars sets --tg-viewport-*
  // asynchronously and expand() settles a beat after mount(). Re-assert layout now,
  // on every viewport change, and via delayed nudges — so the bottom nav + cards
  // show on first load WITHOUT a manual scroll (the reported bug).
  forceReflow();
  try {
    viewport.height.sub(forceReflow);
    viewport.stableHeight.sub(forceReflow);
  } catch {
    /* signals unavailable outside Telegram */
  }
  // expand() resolves on Telegram's side a moment later — nudge again as it settles.
  setTimeout(forceReflow, 120);
  setTimeout(forceReflow, 500);

  // KEYBOARD-AWARE HEIGHT: when the on-screen keyboard opens, lift the whole app
  // (bottom nav + chat input) ABOVE it. See applyKeyboardInset — bound to the
  // live visual viewport so it recomputes on every keyboard open/close + resize.
  try {
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", applyKeyboardInset);
      vv.addEventListener("scroll", applyKeyboardInset);
    }
  } catch {
    /* no visualViewport — layout keeps the stable --app-h */
  }
  try {
    // The keyboard detection compares against Telegram's stable height, so also
    // recompute when THAT changes (rotation / expand settle / fullscreen flip).
    viewport.stableHeight.sub(applyKeyboardInset);
  } catch {
    /* signal unavailable outside Telegram */
  }
  applyKeyboardInset();

  // A STALE TELEGRAM HEIGHT (card-f5 item 8) — see `isStableHeightStale`. Decided now (a reload's restored state
  // paints right the first time), on every window `resize` — which also carries every change of Telegram's stable
  // height, relayed by the `forceReflow` subscription above — on the visual viewport's (an iOS keyboard leaves the
  // window as it is), and when fullscreen or «expanded» flips. ⚠️ Those two need their OWN subscriptions: fullscreen
  // carries no height, and inside the height relay the SDK's `isExpanded` still reads the old value.
  try {
    viewport.isFullscreen.sub(syncFullWindowHeight);
    viewport.isExpanded.sub(syncFullWindowHeight);
  } catch {
    /* signal unavailable outside Telegram */
  }
  try {
    window.addEventListener("resize", syncFullWindowHeight);
    window.visualViewport?.addEventListener("resize", syncFullWindowHeight);
  } catch {
    /* no DOM — ignore */
  }
  syncFullWindowHeight();

  // CACHED-WEBVIEW SELF-HEAL: Telegram reuses a cached webview across entry
  // points (profile "Open", attach menu, direct link) WITHOUT reloading the page
  // — so this boot code may not run again, and the instance can come back
  // windowed/compact. Re-assert fullscreen (unless the user deliberately
  // minimized via the chip this session) + the expanded fallback + layout
  // whenever the page becomes visible again or the stable height moves.
  try {
    viewport.stableHeight.sub(ensureExpanded);
  } catch {
    /* signal unavailable outside Telegram */
  }
  try {
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        ensureFullscreen();
        ensureExpanded();
        forceReflow();
      }
    });
  } catch {
    /* no DOM — ignore */
  }
}

/** Whether to request Bot-API-8 fullscreen for a given Telegram platform. MOBILE
 * (`ios`/`android`) only — on desktop (`tdesktop`/`macos`) and web, fullscreen covers
 * the whole monitor and stretches the mobile-first layout (the desktop-responsiveness
 * bug), so they keep their default medium maximized window. Pure + exported for tests. */
export function fullscreenAllowedFor(platform: string | null): boolean {
  return platform === "ios" || platform === "android";
}

/** The phone clients the full-height fix (`isStableHeightStale`, card-f5 item 8) runs on: ios / android — and
 * Telegram X, which reports itself as `android_x` (card-41 item 3: the fix never ran there while it shared
 * `fullscreenAllowedFor`). Telegram X's fullscreen request still follows `fullscreenAllowedFor`, unchanged.
 * Pure + exported for tests. */
export function fullWindowFixFor(platform: string | null): boolean {
  return fullscreenAllowedFor(platform) || platform === "android_x";
}

/** The Telegram header color per resolved theme: the dark room surface
 * (#0f1412 — Tailwind `surface` dark) or the Royal Mint light surface
 * (#f8f9ff). Pure + exported for unit tests. */
export function headerColorFor(theme: ResolvedTheme): `#${string}` {
  return theme === "dark" ? "#0f1412" : "#f8f9ff";
}

/** Paint Telegram's own header bar for the given theme (no-op outside
 * Telegram / when the method is unavailable). Registered as an
 * {@link onThemeChange} listener so the chrome follows the «پوسته» toggle. */
function applyHeaderColor(theme: ResolvedTheme): void {
  try {
    if (miniApp.setHeaderColor.isAvailable()) miniApp.setHeaderColor(headerColorFor(theme));
  } catch {
    /* unavailable — ignore */
  }
}

/** Set when the user deliberately exits fullscreen via the MinimizeButton chip —
 * the self-heal must not fight that choice for the rest of the session. */
let userExitedFullscreen = false;

/** Whether to request Bot-API-8 fullscreen at all: MOBILE (ios/android) only. Set by
 * setupViewport from the launch platform. Desktop/web stay in the default medium
 * window (fullscreen there stretches the mobile layout — the desktop-responsive bug).
 * Gates BOTH the initial request and the cached-webview self-heal. */
let allowFullscreen = false;

/** Whether the full-height fix may run — `fullWindowFixFor(platform)`, set by setupViewport. */
let fullWindowPhone = false;

/** Request Bot-API-8 fullscreen, retrying ONCE on a transient failure. iOS in
 * particular can drop a request that lands before the client finishes presenting
 * the WebView. UNSUPPORTED and ALREADY_FULLSCREEN are terminal — expand()
 * already covers the former. */
function requestFullscreenWithRetry(attempt = 0): void {
  if (!allowFullscreen) return; // desktop/web: keep the default medium window (responsive)
  try {
    if (!viewport.requestFullscreen.isAvailable()) return;
    void viewport.requestFullscreen().catch((err: unknown) => {
      if (attempt === 0 && !/UNSUPPORTED|ALREADY_FULLSCREEN/i.test(String(err))) {
        setTimeout(() => requestFullscreenWithRetry(1), 400);
      }
    });
  } catch {
    /* unavailable — the expand() fallback still applies */
  }
}

/** Idempotent fullscreen re-assert for cached-webview re-activation: request
 * only when currently windowed AND the user hasn't deliberately minimized this
 * session. Safe anywhere — no-ops outside Telegram. */
function ensureFullscreen(): void {
  if (userExitedFullscreen) return;
  try {
    if (!viewport.isFullscreen()) requestFullscreenWithRetry();
  } catch {
    /* signal unavailable — ignore */
  }
}

/** DESKTOP convergence (see setupViewport): exit fullscreen if Telegram launched us
 * into it (external entry points can), so every desktop entry point matches the clean
 * windowed medium window the in-DM launch uses. Only ever CALLED on desktop
 * (allowFullscreen is false there); guards `isFullscreen` so it's a safe no-op when
 * already windowed / the signal is unavailable. Does NOT touch userExitedFullscreen —
 * that flag is the MOBILE minimize chip, a separate concern. */
function keepWindowedOnDesktop(): void {
  try {
    if (viewport.isFullscreen() && viewport.exitFullscreen.isAvailable()) {
      void viewport.exitFullscreen().catch(() => undefined);
    }
  } catch {
    /* not fullscreen / signal unavailable — ignore */
  }
}

/** Idempotent expand: re-request full height only when Telegram reports the
 * viewport is NOT expanded (a re-activated cached webview, a collapse). Safe to
 * call from any signal — no-ops outside Telegram and when already expanded. */
function ensureExpanded(): void {
  try {
    if (viewport.expand.isAvailable() && !viewport.isExpanded()) viewport.expand();
  } catch {
    /* unavailable — ignore */
  }
}

/**
 * The bottom inset MINUS whatever the CSS layout viewport has ALREADY excluded.
 *
 * ⚠️ THE BOTTOM SIDE IS NOT LIKE THE OTHER THREE, AND RESERVING IT THE SAME WAY DOUBLE-COUNTED IT
 * ON ANDROID. `--app-h` is `min(--tg-viewport-stable-height, 100dvh)`. On iOS both arms are the
 * screen, so the app box IS the screen and the home indicator sits INSIDE it — that inset has to
 * be padded. On Android `100dvh` is the SMALLER arm: the staging viewport probe measured
 * `stable − --app-h` at a constant ~24.4px on a real device in BOTH keyboard states (847.64/823 at
 * rest, 489.24/465 while typing) against 0/0 on the iPhone. That is the gesture-nav bar, the app
 * box already stops above it, and padding `safeAreaInsets.bottom` on top reserved the same bar
 * twice — ~48px of layout for a 24px bar, which is the wasted band under the waiting-room sheet
 * (worse on 3-button nav, where it is 48 + 48).
 *
 * So subtract the exclusion. This can only ever REDUCE the inset, never grow it, and where the two
 * heights agree — iOS, desktop, a plain browser — it is a no-op BY CONSTRUCTION. Every consumer
 * still reads `max(env(safe-area-inset-bottom), var(--tg-safe-bottom))`, so a client that reports
 * the inset through `env()` instead is unaffected either way.
 *
 * Pure + exported so the whole space is unit-tested without a browser; `env()` is 0 and the SDK
 * does not run in a harness, so this is the only place these numbers can be pinned.
 */
export function netBottomInset(
  rawBottom: number,
  stableHeight: number,
  layoutHeight: number,
): number {
  if (!Number.isFinite(rawBottom) || rawBottom <= 0) return 0;
  // A height we cannot trust must never NET — `stableHeight` is published as 0 whenever the
  // viewport state is not stable, and netting against that would subtract the entire layout
  // height and silently drop a real inset to zero (content onto the iOS home indicator).
  if (!Number.isFinite(stableHeight) || !Number.isFinite(layoutHeight)) return rawBottom;
  if (stableHeight <= 0 || layoutHeight <= 0) return rawBottom;
  return Math.max(0, rawBottom - Math.max(0, stableHeight - layoutHeight));
}

/**
 * Publish Telegram's safe-area insets to CSS custom properties on <html>:
 * `--tg-safe-{top,right,left}` = safeAreaInsets (device notch / status bar /
 * home indicator) + contentSafeAreaInsets (Telegram's own header/chrome), per side.
 * `--tg-safe-bottom` is the same sum passed through `netBottomInset` — see there for why the
 * bottom is the one side that must not be reserved as reported.
 * Layout consumes them as `max(env(safe-area-inset-*), var(--tg-safe-*))`, so
 * whichever channel the client actually reports through wins (keeps the bottom
 * nav clear of the iOS home indicator). The SDK defaults both signals to
 * `{top:0,right:0,bottom:0,left:0}` so this reads 0 (no-op vs the :root defaults)
 * outside Telegram / before the first `safe_area_changed` event. Also nudges
 * layout, since a safe-area change reflows the shell.
 */
/**
 * Has a TOP inset ever arrived that we are entitled to believe?
 *
 * ⚠️ **A ZERO FROM THE SDK'S OWN DEFAULT IS NOT A MEASUREMENT, AND PUBLISHING IT WIPED THE SEED.**
 * `applySafeAreaVars()` is called EAGERLY, right after `mount()` — before Telegram has necessarily
 * answered `safe_area_changed` — and the SDK defaults both inset signals to `{top:0,…}`. So the
 * first publish wrote `--tg-safe-top: 0px` over the value `seedSafeAreaTop` had just restored, and
 * the screen dropped again the moment the real number arrived: the fix was inert.
 *
 * Measured, not reasoned: `probe-entry.cjs MODE=returning` still read all three podium columns
 * DOWN 57.6px with the store primed, and the filter grid still jumped 54.8 → 134 in one frame.
 *
 * So a top of 0 is only published once we have a reason to trust it — a real signal, or the
 * settle timer below. A device that genuinely has no top inset therefore carries a stale seed for
 * at most that timer, never for the session.
 */
let topInsetTrusted = false;

function applySafeAreaVars(fromSignal = false): void {
  try {
    const sa = viewport.safeAreaInsets();
    const csa = viewport.contentSafeAreaInsets();
    const root = document.documentElement.style;
    const top = sa.top + csa.top;
    if (fromSignal || top > 0) topInsetTrusted = true;
    if (topInsetTrusted) {
      root.setProperty("--tg-safe-top", `${top}px`);
      rememberTopInset(top);
    }
    root.setProperty("--tg-safe-right", `${sa.right + csa.right}px`);
    // Its own try/catch: a `stableHeight()` that throws must cost the NETTING, never the other
    // three insets (a fragile guard bailing before the rest is a documented past regression here).
    let stableHeight = Number.NaN;
    try {
      stableHeight = viewport.stableHeight();
    } catch {
      /* unavailable — netBottomInset declines to net and publishes the inset as reported */
    }
    root.setProperty(
      "--tg-safe-bottom",
      `${netBottomInset(sa.bottom + csa.bottom, stableHeight, window.innerHeight)}px`,
    );
    root.setProperty("--tg-safe-left", `${sa.left + csa.left}px`);
  } catch {
    /* signals/DOM unavailable — CSS keeps the :root 0px defaults + native env() */
  }
  forceReflow();
}

const SAFE_TOP_KEY = "bing.safeTop";

/**
 * ── WHY THE WHOLE APP DROPS A CENTIMETRE A MOMENT AFTER IT OPENS ───────────────────────────────
 *
 * `--tg-safe-top` is 0 in `:root` and STAYS 0 until Telegram answers `safe_area_changed` over the
 * JS bridge — which is after `viewport.mount()`, i.e. well after the first screen has painted. The
 * shell pads with `max(env(safe-area-inset-top), var(--tg-safe-top))`, and in fullscreen iOS
 * ZEROES the `env()` channel, so on exactly the devices that have a notch the padding goes
 * 0 → ~80px mid-entry and every screen slides down under whatever it was doing.
 *
 * It reads as an animation fault — «the player photos catch at the top and drop down» — and it is
 * not one: measured with a rAF trajectory recorder on the deployed build, the podium's photo and
 * its plinth keep a CONSTANT gap through the whole entrance (116/96/92px, one value each across
 * 85 samples) and rise together. Injecting a late `--tg-safe-top` reproduces the drop exactly, on
 * all three columns at once, at the same 56.6px — because it is the SHELL moving, not the podium.
 *
 * So the last known top inset is remembered and re-applied BEFORE the first paint. The device is
 * overwhelmingly the same device with the same notch and the same launch mode, so the seeded value
 * is normally the one the bridge is about to report; when it is not, the bridge corrects it in the
 * same place it always did. This does not remove the jump on a FIRST-EVER open (there is nothing
 * to remember yet) and it is not meant to: it removes it on every open after that.
 *
 * ⚠️ ONLY THE TOP. The bottom inset is netted against the stable height (`netBottomInset`), which
 * is not knowable before the viewport mounts, and seeding a wrong bottom would push the nav up
 * over the content rather than merely mis-padding a header. Left/right are 0 on every device this
 * app has ever seen.
 *
 * ⚠️ Storage is wrapped end to end: a private window, blocked site data or a storage-less WebView
 * throws on ACCESS, and this runs before anything else in the boot chain.
 */
function rememberTopInset(px: number): void {
  try {
    if (!Number.isFinite(px) || px < 0) return;
    window.localStorage.setItem(SAFE_TOP_KEY, String(Math.round(px)));
  } catch {
    /* storage unavailable — the seed simply never fires, which is today's behaviour */
  }
}

/** Re-apply the last known top inset. Call SYNCHRONOUSLY at boot, before the first render. */
export function seedSafeAreaTop(): void {
  try {
    const raw = window.localStorage.getItem(SAFE_TOP_KEY);
    if (raw === null) return;
    const px = Number(raw);
    // A stored value the app could not have produced is discarded rather than trusted: 200px of
    // phantom padding at the top of every screen is a worse failure than the jump it replaces.
    if (!Number.isFinite(px) || px <= 0 || px > 160) return;
    document.documentElement.style.setProperty("--tg-safe-top", `${px}px`);
  } catch {
    /* storage unavailable */
  }
}

/** Bind `--app-h` to the KEYBOARD-adjusted visible height while the on-screen
 * keyboard is open, then release it when the keyboard closes. Fixes the Android
 * "bottom nav / chat input hidden UNDER the keyboard" bug: `--app-h` defaults to
 * Telegram's STABLE height, which by design ignores the keyboard, so the shell
 * stayed full-height and its bottom slid behind the keyboard.
 *
 * Detection is mode-agnostic: `window.visualViewport.height` reports the actually
 * visible area (excluding the keyboard) in BOTH Android keyboard modes (resize +
 * pan) AND on iOS; the stable height is the reliable "as if no keyboard"
 * reference (innerHeight is NOT — it shrinks in resize mode). Their difference is
 * the keyboard height. While it's meaningful we pin `--app-h` to the visible
 * height (nav + input lift above the keyboard); otherwise we clear the inline
 * override so `--app-h` falls back to its `:root` stable-height definition —
 * i.e. the normal boot/expand behaviour is completely untouched when no keyboard
 * is up. No-op where `visualViewport` is unavailable. */
/** The last `--app-h` we wrote (null = released to the CSS default). */
let lastKeyboardHeight: string | null = null;
/** Whether the keyboard was up on the previous pass (an OPEN/CLOSE edge, not a frame). */
let keyboardWasOpen = false;

/**
 * THE CHAT "LAGGY KEYBOARD" BUG.
 *
 * `visualViewport` fires `resize` AND `scroll` many times per second while the keyboard
 * animates open or shut. This ran the whole body on every one of those events — and its
 * last line was `forceReflow()`, which (a) reads `offsetHeight` to force a SYNCHRONOUS
 * full-document layout and (b) dispatches a synthetic `resize`, which `useViewportReflow`
 * turns into a re-render of the ENTIRE React tree. So a single keyboard open cost dozens
 * of forced layouts + whole-app re-renders — worst exactly where the tree is heaviest (a
 * chat thread, with the emoji grid in it). That is the stutter.
 *
 * Now: coalesced to at most once per frame; the CSS var is written only when the value
 * really changed (the `scroll` storm mostly reports the SAME height); and the expensive
 * wake-everything reflow fires only on the OPEN↔CLOSE edge, not on every animation frame.
 * The var write alone is enough to keep the layout tracking the keyboard — the browser
 * relayouts natively, without us forcing it.
 */
function applyKeyboardInset(): void {
  // Run INLINE, not on the next frame. `visualViewport` already reports inside a frame,
  // and hopping through rAF put our height one frame BEHIND the keyboard — the content
  // visibly trailing it. The work here is a single CSS-variable write; the browser lays
  // out at paint time anyway.
  syncKeyboardInset();
}

/** A keyboard must swallow at least this much height to count (vs. chrome/URL-bar noise). */
const KEYBOARD_MIN_PX = 120;

/**
 * What the on-screen keyboard means for our layout — the whole decision, as a pure
 * function of three heights, so it can be tested without a device.
 *
 * `appHeight` is the px height to pin `--app-h` to, or NULL to leave it to CSS:
 *
 *  - **The browser already did it** (`layoutHeight` tracks the visible area — Chrome/
 *    Android with `interactive-widget=resizes-content`): CSS `--app-h: min(stable, 100dvh)`
 *    follows the shrunken layout viewport natively, on the browser's own timeline, and it
 *    measures the keyboard exactly as the platform does — INCLUDING the copy/paste/emoji
 *    toolbar that was overlapping the bottom nav. We must not fight that with a JS write.
 *  - **iOS** ignores `interactive-widget`: the layout viewport keeps its full height while
 *    the visual viewport shrinks, so writing the height ourselves is the only way.
 */
export function keyboardLayout(
  stableHeight: number,
  visualHeight: number,
  layoutHeight: number,
): { open: boolean; appHeight: number | null } {
  const open = stableHeight - visualHeight > KEYBOARD_MIN_PX;
  const nativelyResized = Math.abs(layoutHeight - visualHeight) <= 2;
  return {
    open,
    appHeight: open && !nativelyResized ? Math.round(visualHeight) : null,
  };
}

function syncKeyboardInset(): void {
  try {
    const vv = window.visualViewport;
    if (!vv) return;
    const docEl = document.documentElement;
    const root = docEl.style;
    let stable = 0;
    try {
      stable = viewport.stableHeight();
    } catch {
      stable = 0;
    }
    if (!stable) stable = docEl.clientHeight || window.innerHeight || 0;

    const { open, appHeight } = keyboardLayout(stable, vv.height, docEl.clientHeight);
    const next = appHeight === null ? null : `${appHeight}px`;

    if (next !== lastKeyboardHeight) {
      lastKeyboardHeight = next;
      if (next) root.setProperty("--app-h", next);
      else root.removeProperty("--app-h");
    }
    if (open !== keyboardWasOpen) {
      keyboardWasOpen = open;
      // One flag, read by CSS: it hides the bottom nav while typing (the element Android's
      // keyboard toolbar was covering, and dead weight in a composer anyway).
      if (open) docEl.dataset["keyboard"] = "open";
      else delete docEl.dataset["keyboard"];
      forceReflow(); // ONCE per open/close — never per animation frame
    }
  } catch {
    /* no DOM / no visualViewport — ignore */
  }
}

/**
 * ⚠️ A STALE TELEGRAM HEIGHT MUST NEVER DRAW THE APP SHORT IN A FULL WINDOW (card-f5 item 8; the operator,
 * 2026-10-13: «When the window is full height, the table must use the real height, not a stale short one left over
 * from the keyboard or from a refused rejoin»).
 *
 * Every height here is `min(--tg-viewport-stable-height, 100dvh)`, and Telegram's stable height is only as fresh as
 * its last `is_state_stable` event. Two phones drew their table far short of a FULL window: 497.8 px of 832 — the
 * keyboard-open height (the stable height drops with the keyboard; Android's back button hides the keyboard without a
 * blur, and no fresh stable height came back) — and ≤ 317 px of 740 right after an app return (a fresh WebView is
 * answered with the SHEET's height and then goes fullscreen without a new one; a RELOAD asks nothing at all — the SDK
 * restores the last page's state from sessionStorage). The board-height freeze then accepts the short value as a new
 * rest after `SETTLE_MS`, and the table re-fits to it. Replayed in `test/stale-height-*.test.ts`.
 *
 * So the window's height is used when the window is at FULL HEIGHT and Telegram's stable height is short of it:
 *  · full height = a phone client (ios/android/android_x — `fullWindowFixFor`: Telegram Desktop and the web clients
 *    NEVER — exactly today's there) in fullscreen or an expanded sheet, with nothing over the window — the visual
 *    viewport IS the window;
 *  · NOT a collapsed sheet: Telegram shows its top part with the rest off-screen, and a short height is the truth;
 *  · NOT with a keyboard up: an iOS keyboard covers the window (the visual viewport shrinks, the window does not); on
 *    Android the WebView itself shrinks, so `100dvh` already IS the part above the keyboard and Telegram's fresh
 *    keyboard height agrees with it. The freeze holds the board while a field has focus, as before;
 *  · decided from the window and the visual viewport, never from focus: «no focused input» is not «no keyboard».
 *
 * The flag is `--full-window-h: 100dvh` on <html>, which `--app-h` and `--viewport-h` (theme.css) read IN FRONT of
 * Telegram's variable: the SDK owns `--tg-viewport-stable-height` and rewrites it on every change, and `--viewport-h`
 * must stay `--app-h`'s expression, never pinned. Unset — everywhere but this one state — both are today's exactly.
 */
export const FULL_WINDOW_VAR = "--full-window-h";

/** A stable height this far short of the window is stale, not rounding. */
const STALE_MIN_PX = 2;

/** The decision, pure. */
export function isStableHeightStale(input: {
  /** A phone client — `fullWindowFixFor(platform)`: ios / android / android_x (Telegram X). */
  phone: boolean;
  fullscreen: boolean;
  expanded: boolean;
  /** Telegram's stable viewport height (the SDK's `stableHeight`). */
  stableHeight: number;
  /** `window.innerHeight` — the layout viewport, `100dvh`. */
  windowHeight: number;
  /** `visualViewport.height`; null without a visual viewport (then nothing can tell a keyboard: today's). */
  visualHeight: number | null;
}): boolean {
  const { phone, fullscreen, expanded, stableHeight, windowHeight, visualHeight } = input;
  if (!phone || !(fullscreen || expanded)) return false;
  // Something covers the window — an iOS keyboard — or a number that cannot be read (NaN fails every comparison):
  // not known to be at full height.
  if (visualHeight === null || !(visualHeight >= windowHeight - STALE_MIN_PX)) return false;
  return stableHeight < windowHeight - STALE_MIN_PX;
}

/** Whether `--full-window-h` is set now — it is written only when the decision flips. */
let fullWindowApplied = false;

function syncFullWindowHeight(): void {
  try {
    const vv = window.visualViewport;
    const stale = isStableHeightStale({
      phone: fullWindowPhone,
      fullscreen: viewport.isFullscreen(),
      expanded: viewport.isExpanded(),
      stableHeight: viewport.stableHeight(),
      windowHeight: window.innerHeight,
      visualHeight: vv ? vv.height : null,
    });
    if (stale === fullWindowApplied) return;
    fullWindowApplied = stale; // BEFORE the reflow: its synthetic `resize` comes straight back here
    const root = document.documentElement.style;
    if (stale) root.setProperty(FULL_WINDOW_VAR, "100dvh");
    else root.removeProperty(FULL_WINDOW_VAR);
    forceReflow(); // once per flip: measure-on-resize code re-reads the app's new height
  } catch {
    /* no DOM / a signal unavailable — nothing changes: today's */
  }
}

/** Current fullscreen state (false if the signal isn't available/mounted). */
export function isFullscreenNow(): boolean {
  try {
    return viewport.isFullscreen();
  } catch {
    return false;
  }
}

/** Exit fullscreen ("minimize" back to the standard, still-expanded view — where
 *  Telegram's own header/close is reachable). Since we disable the swipe-down, the
 *  in-app minimize button is the way out — and using it marks the choice so the
 *  cached-webview self-heal stops re-entering fullscreen this session. */
export function exitFullscreen(): void {
  userExitedFullscreen = true;
  try {
    if (viewport.exitFullscreen.isAvailable()) {
      void viewport.exitFullscreen().catch(() => {
        /* already windowed / not supported */
      });
    }
  } catch {
    /* ignore */
  }
}

/** React hook: live fullscreen flag, subscribed to the SDK signal. */
export function useIsFullscreen(): boolean {
  const [fs, setFs] = useState<boolean>(() => isFullscreenNow());
  useEffect(() => {
    let unsub: (() => void) | undefined;
    try {
      unsub = viewport.isFullscreen.sub(() => setFs(isFullscreenNow()));
    } catch {
      /* signal unavailable */
    }
    return () => unsub?.();
  }, []);
  return fs;
}

/** Force the browser (and any resize listeners) to recompute layout NOW. Telegram
 * binds --tg-viewport-* asynchronously and expand() settles a beat after mount,
 * so the WebView otherwise keeps the stale first-paint layout until a manual
 * scroll. */
function forceReflow(): void {
  try {
    void document.documentElement.offsetHeight; // sync reflow against the fresh vars
    window.dispatchEvent(new Event("resize")); // wake any measure-on-resize code
  } catch {
    /* no DOM — ignore */
  }
}

/** Re-render the caller whenever Telegram's viewport (stable) height OR safe-area
 * changes, so React-computed layout refreshes alongside the CSS vars — belt-and-
 * suspenders for the first-paint settle + rotation/keyboard. Call once at the
 * app root. */
/**
 * Everything this hook exists to react to, as one comparable string: the STABLE
 * viewport height, the safe-area insets, and the width. Deliberately NOT the live
 * viewport height — that is the value the KEYBOARD moves.
 */
function layoutSignature(): string {
  let stable = 0;
  let safe = "";
  let content = "";
  try {
    stable = Math.round(viewport.stableHeight());
  } catch {
    /* signal unavailable */
  }
  try {
    const i = viewport.safeAreaInsets();
    safe = `${i.top},${i.right},${i.bottom},${i.left}`;
  } catch {
    /* signal unavailable */
  }
  try {
    const i = viewport.contentSafeAreaInsets();
    content = `${i.top},${i.right},${i.bottom},${i.left}`;
  } catch {
    /* signal unavailable */
  }
  const width = typeof window === "undefined" ? 0 : window.innerWidth;
  return `${stable}|${width}|${safe}|${content}`;
}

/**
 * Re-render the caller when Telegram's STABLE viewport height, safe-area insets or
 * width change (first-paint settle, rotation, fullscreen flip), so React-computed
 * layout refreshes alongside the CSS vars. Call once at the app root.
 *
 * THE CHAT "LAGGY KEYBOARD" BUG, PART 2. This used to subscribe to `viewport.height`
 * — the LIVE height, i.e. the exact value the keyboard moves — and to every
 * `window.resize`, and each one called `bump()`: a state update on the app ROOT, which
 * re-renders the WHOLE tree (the screen, the chat thread and its message list, the
 * bottom nav…). A keyboard opening or closing fires those events repeatedly, so the app
 * re-rendered itself over and over DURING the transition. That is what made the page
 * stutter as it went from half-height back to full.
 *
 * The first fix compared a SIGNATURE built on the STABLE height, on the premise that "a keyboard
 * changes neither the stable height nor the safe area".
 *
 * ⚠️ THAT PREMISE IS FALSE, measured on two real phones (staging viewport probe, 2026-07-31):
 * Telegram's stable height DROPS with the keyboard on BOTH platforms — iOS 932→614, Android
 * 848→489. So the signature moved on every keyboard open AND close and the whole tree re-rendered
 * anyway — which is what made a board visibly re-lay-out ("load") when the keyboard closed, even
 * after its HEIGHT was being held still. A geometry check is blind to this: the layout is identical
 * before and after; what the eye catches is the re-render replaying the entrance motion.
 *
 * So the signature is IGNORED while a text field owns focus — the only keyboard signal that behaved
 * on both devices (see board-height.ts). A rotation or fullscreen flip still refreshes as before,
 * and one that happens mid-typing refreshes as soon as focus leaves, because the same subscriptions
 * fire again when the height comes back.
 */
/** How many times the app ROOT has been re-rendered by this hook. Read by the staging viewport
 *  probe: a re-render moves nothing, so a geometry check is blind to it — but the eye sees the
 *  entrance motion replay, which is what «the board re-lays out on close» was. */
let reflowBumps = 0;
export function reflowBumpCount(): number {
  return reflowBumps;
}

export function useViewportReflow(): void {
  const [, bump] = useState(0);
  useEffect(() => {
    let raf = 0;
    let last = layoutSignature();
    const check = (): void => {
      if (raf) return; // one check per frame — the events can arrive in bursts
      raf = requestAnimationFrame(() => {
        raf = 0;
        // A focused text field IS the keyboard. Everything the signature reads moves with it, so
        // while typing there is nothing here worth re-rendering for — and re-rendering the ROOT
        // replays every entrance animation underneath it.
        //
        // ⚠️ FOCUS ALONE IS NOT ENOUGH, AND THAT GAP WAS VISIBLE ON AN IPHONE: focus ends at the
        // START of the dismissal, so the whole CLOSE — where the height climbs back — happened with
        // the guard already off, bumping the root twice. `isBoardHeightPinned()` stays true from
        // focus until the height has actually come back, so it covers the close as well.
        if (isTextEntry(document.activeElement) || isBoardHeightPinned()) return;
        const next = layoutSignature();
        if (next === last) return; // nothing we actually lay out against moved
        last = next;
        reflowBumps += 1;
        bump((n) => n + 1);
      });
    };
    const unsubs: Array<() => void> = [];
    try {
      unsubs.push(viewport.stableHeight.sub(check));
      unsubs.push(viewport.safeAreaInsets.sub(check));
      unsubs.push(viewport.contentSafeAreaInsets.sub(check));
    } catch {
      /* signals unavailable */
    }
    window.addEventListener("resize", check);
    return () => {
      unsubs.forEach((u) => u());
      window.removeEventListener("resize", check);
      cancelAnimationFrame(raf);
    };
  }, []);
}
